import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AGENT_PROMPT } from '../components/AgentLauncher'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import TemplateCard from '../components/TemplateCard'
import { useCopyPrompt } from '../hooks/useCopyPrompt'
import { toCardData } from '../lib/templateMap'
import { TEMPLATE_CATEGORIES } from '../lib/templateTypes'
import type { TemplateCategory } from '../lib/templateTypes'
import { templatesApi, type TemplateSummaryDTO } from '../services/api'
import '../styles/templates.css'

const PAGE_TITLE = 'Website templates — start from a prompt | OpenDeploy'
const PAGE_DESCRIPTION =
  'Templates that are prompts, not repos to clone. Copy one into your coding agent — it builds the site and deploys it with /opendeploy, ending in a live URL.'

function buildJsonLd(templates: TemplateSummaryDTO[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': 'https://opendeploy.dev/templates#list',
    name: 'OpenDeploy website templates',
    description: PAGE_DESCRIPTION,
    numberOfItems: templates.length,
    itemListElement: templates.map((template, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${template.name} website template`,
      url: `https://opendeploy.dev/templates/${template.slug}`,
    })),
  }
}

function pillLabel(category: TemplateCategory) {
  return category === 'Personal portfolio' ? 'portfolio' : category.toLowerCase()
}

// Gallery renders in batches and grows as the sentinel scrolls into view, so a
// large gallery doesn't mount every card (and its live-preview iframe) at once.
const GALLERY_BATCH = 12

export default function Templates() {
  const location = useLocation()
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<TemplateSummaryDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all')
  const [visibleCount, setVisibleCount] = useState(GALLERY_BATCH)
  const sentinelRef = useRef<HTMLDivElement>(null)
  // Read the unknown-slug redirect state once, at mount — ephemeral by design.
  const [noticeVisible, setNoticeVisible] = useState(() =>
    Boolean((location.state as { missingSlug?: string } | null)?.missingSlug),
  )
  const { copied, copyFailed, copy } = useCopyPrompt()
  const bandPromptRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let active = true
    templatesApi
      .list()
      .then((res) => {
        if (!active) return
        setTemplates(res.data || [])
        setLoadError(false)
      })
      .catch(() => {
        if (active) setLoadError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // Total copy failure: select the (visually hidden) starter prompt in the
  // band so "press ⌘C" actually copies AGENT_PROMPT.
  useEffect(() => {
    if (!copyFailed) return
    const node = bandPromptRef.current
    const selection = window.getSelection()
    if (!node || !selection) return

    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }, [copyFailed])

  useEffect(() => {
    if (!noticeVisible) return
    // Consume the router state so refresh / back-forward never re-shows it,
    // then auto-dismiss after 6s.
    navigate('/templates', { replace: true, state: null })
    const timer = window.setTimeout(() => setNoticeVisible(false), 6000)
    return () => window.clearTimeout(timer)
    // Mount-only: the notice is consumed exactly once.
  }, [])

  const selectCategory = (category: TemplateCategory | 'all') => {
    setActiveCategory(category)
    setVisibleCount(GALLERY_BATCH) // reset the lazy window when the filter changes
    setNoticeVisible(false)
  }

  const visibleTemplates = useMemo(
    () =>
      activeCategory === 'all'
        ? templates
        : templates.filter((template) => template.category === activeCategory),
    [templates, activeCategory],
  )

  const shownTemplates = visibleTemplates.slice(0, visibleCount)
  const hasMore = visibleCount < visibleTemplates.length

  // Grow the visible window when the sentinel nears the viewport. Re-runs on
  // each bump so a tall viewport keeps filling until the sentinel is offscreen.
  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      if (node) setVisibleCount((c) => c + GALLERY_BATCH)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => c + GALLERY_BATCH)
        }
      },
      { rootMargin: '600px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, visibleCount, visibleTemplates.length])

  const jsonLd = useMemo(() => buildJsonLd(templates), [templates])

  return (
    <>
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        path="/templates"
        jsonLd={jsonLd}
      />

      <div className="od-tpl-page">
        <SiteNav />
        <main className="od-tpl-frame">
          {noticeVisible && (
            <p className="od-tpl-notice" role="status">
              That template doesn’t exist — here’s everything we have.
            </p>
          )}

          <header className="od-tpl-hero od-tpl-fade-1">
            <p className="od-tpl-kicker">templates</p>
            <h1 id="od-tpl-hero-title">Start from a prompt</h1>
            <p className="od-tpl-hero-sub">
              A template here isn’t a repo you clone — it’s a prompt your agent
              runs, builds, and deploys. Pick one below and copy its prompt.
            </p>
            <p className="od-tpl-loop">
              <b>prompt</b>
              <span aria-hidden="true">→</span>
              <b>your agent builds</b>
              <span aria-hidden="true">→</span>
              <b>/opendeploy</b>
              <span aria-hidden="true">→</span>
              <b>live URL</b>
            </p>
          </header>

          <section aria-labelledby="od-tpl-all-title">
            <div className="od-tpl-gallery-head">
              <h2 id="od-tpl-all-title">All templates</h2>
              <div
                className="od-tpl-pills"
                role="group"
                aria-label="Filter templates by category"
              >
                <button
                  type="button"
                  className="od-tpl-pill"
                  aria-pressed={activeCategory === 'all'}
                  onClick={() => selectCategory('all')}
                >
                  all
                </button>
                {TEMPLATE_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className="od-tpl-pill"
                    aria-pressed={activeCategory === category}
                    onClick={() => selectCategory(category)}
                  >
                    {pillLabel(category)}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className="od-tpl-gallery-status" role="status">
                Loading templates…
              </p>
            ) : loadError ? (
              <p className="od-tpl-gallery-status" role="status">
                Couldn’t load templates right now — please refresh.
              </p>
            ) : visibleTemplates.length === 0 ? (
              <p className="od-tpl-gallery-status" role="status">
                No templates in this category yet.
              </p>
            ) : (
              <>
                <div className="od-tpl-grid">
                  {shownTemplates.map((template, index) => (
                    <TemplateCard
                      key={template.slug}
                      template={toCardData(template)}
                      eager={index < 3}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div
                    ref={sentinelRef}
                    className="od-tpl-gallery-status"
                    role="status"
                  >
                    Loading more templates…
                  </div>
                )}
              </>
            )}
          </section>

          <aside className="od-tpl-band">
            <p>
              Don’t see your site? Describe it to your agent — any prompt
              deploys with <span className="od-tpl-hl">/opendeploy</span>.
            </p>
            <span ref={bandPromptRef} className="v2-sr-only" aria-hidden="true">
              {AGENT_PROMPT}
            </span>
            <button
              type="button"
              className="od-tpl-copy-cta"
              onClick={() => copy(AGENT_PROMPT)}
            >
              {copyFailed ? 'press ⌘C' : copied ? 'copied' : 'copy starter prompt'}
            </button>
            <span className="v2-sr-only" aria-live="polite">
              {copied ? 'Prompt copied' : ''}
            </span>
          </aside>
        </main>
        <Footer />
      </div>
    </>
  )
}
